import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Edit2, Download, FileText, Package, CheckCircle, Upload, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsTable = ({ products, categories, language, onDelete, onRefresh }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/products/export/excel`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `flowingo_produkter_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'sv' ? 'Excel exporterad' : 'Excel exported');
    } catch (e) {
      toast.error(language === 'sv' ? 'Kunde inte exportera Excel' : 'Could not export Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
  <div className="bg-white border border-stone-200 overflow-hidden">
    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
      <h3 className="text-stone-900 font-semibold">{language === 'sv' ? 'Alla produkter' : 'All products'}</h3>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={exporting}
          data-testid="export-products-excel"
          className="border-stone-300 text-stone-700 hover:bg-stone-50 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          {exporting
            ? (language === 'sv' ? 'Exporterar...' : 'Exporting...')
            : (language === 'sv' ? 'Exportera Excel' : 'Export Excel')}
        </Button>
        <ProductFormDialog onSuccess={onRefresh} language={language} categories={categories} allProducts={products} />
      </div>
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead>{language === 'sv' ? 'Namn' : 'Name'}</TableHead>
            <TableHead>{language === 'sv' ? 'Kategori' : 'Category'}</TableHead>
            <TableHead>{language === 'sv' ? 'Varianter' : 'Variants'}</TableHead>
            <TableHead>{language === 'sv' ? 'Dokument' : 'Documents'}</TableHead>
            <TableHead>{language === 'sv' ? 'Populär' : 'Popular'}</TableHead>
            <TableHead className="text-right">{language === 'sv' ? 'Åtgärder' : 'Actions'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            // Find category names from IDs/slugs
            const mainCat = categories.find(c => c.id === product.main_category_id || c.slug === product.main_category_id);
            const mainCatName = mainCat ? (language === 'sv' ? mainCat.name_sv : mainCat.name_en) : product.main_category_id;
            
            let subCatName = '';
            if (product.sub_category_id && mainCat) {
              const subCat = mainCat.children?.find(s => s.id === product.sub_category_id || s.slug === product.sub_category_id);
              subCatName = subCat ? (language === 'sv' ? subCat.name_sv : subCat.name_en) : product.sub_category_id;
            }
            
            const categoryDisplay = subCatName ? `${mainCatName} → ${subCatName}` : mainCatName;
            
            return (
              <TableRow key={product.id} className="hover:bg-stone-50">
                <TableCell className="font-medium">{language === 'sv' ? product.name_sv : product.name_en}</TableCell>
                <TableCell className="text-stone-600">{categoryDisplay}</TableCell>
                <TableCell className="text-center">{product.variants?.length || 0}</TableCell>
                <TableCell className="text-center">
                  <ProductDocumentsDialog product={product} language={language} onRefresh={onRefresh} />
                </TableCell>
                <TableCell className="text-center">{product.is_featured ? <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto" /> : <XCircle className="w-5 h-5 text-stone-300 mx-auto" />}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <ProductFormDialog onSuccess={onRefresh} language={language} categories={categories} editData={product} allProducts={products} />
                    <Button variant="ghost" size="icon" onClick={() => onDelete(product.id)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </div>
  );
};

// Product Documents Dialog
const ProductDocumentsDialog = ({ product, language, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentName, setDocumentName] = useState('');
  
  const documents = product.documents || [];
  
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'sv' ? 'Otillåten filtyp. Använd PDF eller DOC.' : 'Invalid file type. Use PDF or DOC.');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'sv' ? 'Filen är för stor. Max 10MB.' : 'File is too large. Max 10MB.');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_name', documentName || file.name);
      
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/products/${product.id}/documents`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(language === 'sv' ? 'Dokument uppladdat!' : 'Document uploaded!');
      setDocumentName('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'sv' ? 'Fel vid uppladdning' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };
  
  const handleDelete = async (docUrl) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort detta dokument?' : 'Remove this document?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/products/${product.id}/documents?document_url=${encodeURIComponent(docUrl)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(language === 'sv' ? 'Dokument borttaget' : 'Document removed');
      onRefresh();
    } catch (error) {
      toast.error(language === 'sv' ? 'Kunde inte ta bort dokumentet' : 'Could not remove document');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-stone-600 hover:text-stone-900">
          <FileText className="w-4 h-4 mr-1" />
          {documents.length}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-stone-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {language === 'sv' ? 'Produktdokument' : 'Product Documents'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Product name */}
          <p className="text-sm text-stone-600">
            {language === 'sv' ? product.name_sv : product.name_en}
          </p>
          
          {/* Existing documents */}
          {documents.length > 0 ? (
            <div className="space-y-2">
              <Label>{language === 'sv' ? 'Uppladdade dokument' : 'Uploaded documents'}</Label>
              <div className="border border-stone-200 divide-y divide-stone-200">
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 hover:bg-stone-50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-sm font-medium text-stone-900">{doc.name || doc.original_filename || 'Dokument'}</p>
                        <p className="text-xs text-stone-500">{doc.original_filename}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700">
                          <Download className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDelete(doc.url)}
                        className="text-stone-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-stone-500">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>{language === 'sv' ? 'Inga dokument uppladdade' : 'No documents uploaded'}</p>
            </div>
          )}
          
          {/* Upload new document */}
          <div className="border-t border-stone-200 pt-4">
            <Label className="mb-2 block">{language === 'sv' ? 'Ladda upp nytt dokument' : 'Upload new document'}</Label>
            <div className="space-y-3">
              <Input 
                placeholder={language === 'sv' ? 'Dokumentnamn (valfritt)' : 'Document name (optional)'}
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className="bg-white border-stone-300"
              />
              <div className="border-2 border-dashed border-stone-300 hover:border-stone-400 transition-colors p-4 text-center">
                <input 
                  type="file" 
                  id={`doc-upload-${product.id}`}
                  accept=".pdf,.doc,.docx" 
                  onChange={handleUpload} 
                  className="hidden"
                  disabled={uploading}
                />
                <label htmlFor={`doc-upload-${product.id}`} className="cursor-pointer">
                  <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                  {uploading ? (
                    <p className="text-stone-600">{language === 'sv' ? 'Laddar upp...' : 'Uploading...'}</p>
                  ) : (
                    <>
                      <p className="text-stone-700 font-medium text-sm">{language === 'sv' ? 'Klicka för att ladda upp' : 'Click to upload'}</p>
                      <p className="text-stone-500 text-xs">PDF, DOC, DOCX (max 10MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductFormDialog = ({ onSuccess, language, categories, editData = null, allProducts = [] }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importingDoc, setImportingDoc] = useState(false);
  const [name_sv, setNameSv] = useState('');
  const [name_en, setNameEn] = useState('');
  const [desc_sv, setDescSv] = useState('');
  const [desc_en, setDescEn] = useState('');
  const [main_category_id, setMainCatId] = useState('');
  const [sub_category_id, setSubCatId] = useState('');
  const [image_url, setImageUrl] = useState('');
  const [is_featured, setIsFeatured] = useState(false);
  const [accessory_product_ids, setAccessoryIds] = useState([]);
  const [accessorySearch, setAccessorySearch] = useState('');
  const [certification_ids, setCertificationIds] = useState([]);
  const [certSearch, setCertSearch] = useState('');
  const [certifications, setCertifications] = useState([]);
  
  // Document import states
  const [docFile, setDocFile] = useState(null);
  const [docLanguageTarget, setDocLanguageTarget] = useState('sv');
  const [docAction, setDocAction] = useState('replace');

  const subCategories = categories.find(c => c.id === main_category_id)?.children || [];

  useEffect(() => {
    if (editData && open) {
      setNameSv(editData.name_sv || '');
      setNameEn(editData.name_en || '');
      setDescSv(editData.description_sv || '');
      setDescEn(editData.description_en || '');
      setMainCatId(editData.main_category_id || '');
      setSubCatId(editData.sub_category_id || '');
      setImageUrl(editData.image_url || '');
      setIsFeatured(editData.is_featured || false);
      setAccessoryIds(Array.isArray(editData.accessory_product_ids) ? editData.accessory_product_ids : []);
      setCertificationIds(Array.isArray(editData.certification_ids) ? editData.certification_ids : []);
    } else if (open) {
      setNameSv(''); 
      setNameEn(''); 
      setDescSv(''); 
      setDescEn(''); 
      setMainCatId(''); 
      setSubCatId(''); 
      setImageUrl(''); 
      setIsFeatured(false);
      setAccessoryIds([]);
      setAccessorySearch('');
      setCertificationIds([]);
      setCertSearch('');
      setDocFile(null);
      setDocLanguageTarget('sv');
      setDocAction('replace');
    }
  }, [editData, open]);

  // Load certifications when dialog opens
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/certifications?active_only=true`)
      .then((res) => setCertifications(res.data || []))
      .catch(() => setCertifications([]));
  }, [open]);
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData);
      setImageUrl(res.data.url);
      toast.success(language === 'sv' ? 'Bild uppladdad' : 'Image uploaded');
    } catch (error) {
      toast.error(language === 'sv' ? 'Fel vid uppladdning' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDocumentImport = async () => {
    if (!docFile) return;
    
    setImportingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', docFile);
      
      const res = await axios.post(`${API}/extract-document-content`, formData);
      const extractedHtml = res.data.html_content;
      
      // Apply to selected language
      if (docLanguageTarget === 'sv') {
        if (docAction === 'replace') {
          setDescSv(extractedHtml);
        } else {
          setDescSv(desc_sv + '\n\n' + extractedHtml);
        }
      } else {
        if (docAction === 'replace') {
          setDescEn(extractedHtml);
        } else {
          setDescEn(desc_en + '\n\n' + extractedHtml);
        }
      }
      
      toast.success(language === 'sv' ? 'Dokument importerat!' : 'Document imported!');
      setDocFile(null);
    } catch (error) {
      console.error('Document import error:', error);
      toast.error(language === 'sv' ? 'Dokumentimport misslyckades' : 'Document import failed');
    } finally {
      setImportingDoc(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Convert documents to correct format (strings only)
      const documentsFormatted = (editData?.documents || []).map(doc => {
        if (typeof doc === 'string') return doc;
        if (doc && doc.url) return doc.url;
        return doc;
      });
      
      const data = { 
        name_sv, 
        name_en, 
        description_sv: desc_sv, 
        description_en: desc_en, 
        main_category_id, 
        sub_category_id: sub_category_id || null, 
        image_url, 
        is_featured,
        // Preserve meta, variants, and documents from existing data
        meta_sv: editData?.meta_sv || '',
        meta_en: editData?.meta_en || '',
        variants: editData?.variants || [],
        variant_columns: editData?.variant_columns || [],
        documents: documentsFormatted,  // Converted to string format
        accessory_product_ids,
        certification_ids,
      };
      if (editData) {
        await axios.put(`${API}/products/${editData.id}`, data);
        toast.success(language === 'sv' ? 'Produkt uppdaterad' : 'Product updated');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success(language === 'sv' ? 'Produkt skapad' : 'Product created');
      }
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Product save error:', error.response?.data);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        // Pydantic validation errors
        const fields = errorMsg.map(e => e.loc.join('.')).join(', ');
        toast.error(`Validation error: ${fields}`);
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Error saving product');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editData ? <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700"><Edit2 className="w-4 h-4" /></Button> : <Button className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Ny produkt' : 'New product'}</Button>}
      </DialogTrigger>
      <DialogContent className="bg-white border-stone-200 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editData ? (language === 'sv' ? 'Redigera produkt' : 'Edit product') : (language === 'sv' ? 'Skapa produkt' : 'Create product')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Image Upload */}
          <div className="flex items-center gap-4">
            {image_url ? (
              <img 
                src={image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${image_url}` : image_url} 
                alt="Produktbild" 
                className="w-24 h-24 object-cover border-2 border-stone-200" 
              />
            ) : (
              <div className="w-24 h-24 bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                <Package className="w-12 h-12 text-stone-400" />
              </div>
            )}
            <div className="flex-1">
              <Label>{language === 'sv' ? 'Produktbild' : 'Product Image'}</Label>
              <Input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload}
                className="bg-white border-stone-300"
                disabled={uploading}
              />
              {uploading && <p className="text-sm text-stone-500 mt-1">{language === 'sv' ? 'Laddar upp...' : 'Uploading...'}</p>}
              {image_url && <p className="text-sm text-emerald-600 mt-1">✓ {language === 'sv' ? 'Bild uppladdad' : 'Image uploaded'}</p>}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Namn (SV)</Label><Input value={name_sv} onChange={(e) => setNameSv(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Name (EN)</Label><Input value={name_en} onChange={(e) => setNameEn(e.target.value)} required className="bg-white border-stone-300" /></div>
          </div>
          
          {/* Document Import Section */}
          <div className="border border-stone-300 p-4 bg-stone-50 rounded">
            <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              {language === 'sv' ? 'Importera från dokument' : 'Import from document'}
            </h4>
            <p className="text-sm text-stone-600 mb-3">
              {language === 'sv' 
                ? 'Ladda upp Word (.docx) eller PDF för att automatiskt extrahera text, bilder och tabeller till beskrivningen.'
                : 'Upload Word (.docx) or PDF to automatically extract text, images and tables to description.'}
            </p>
            
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">{language === 'sv' ? 'Språk' : 'Language'}</Label>
                <Select value={docLanguageTarget} onValueChange={setDocLanguageTarget}>
                  <SelectTrigger className="bg-white border-stone-300 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="sv">Svenska (SV)</SelectItem>
                    <SelectItem value="en">English (EN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs">{language === 'sv' ? 'Åtgärd' : 'Action'}</Label>
                <Select value={docAction} onValueChange={setDocAction}>
                  <SelectTrigger className="bg-white border-stone-300 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="replace">{language === 'sv' ? 'Ersätt' : 'Replace'}</SelectItem>
                    <SelectItem value="append">{language === 'sv' ? 'Lägg till' : 'Append'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs">{language === 'sv' ? 'Dokument' : 'Document'}</Label>
                <Input 
                  type="file" 
                  accept=".docx,.pdf"
                  onChange={(e) => setDocFile(e.target.files[0])}
                  className="bg-white border-stone-300 h-9 text-sm"
                  disabled={importingDoc}
                />
              </div>
            </div>
            
            <Button
              type="button"
              onClick={handleDocumentImport}
              disabled={!docFile || importingDoc}
              className="bg-[#321a27] hover:bg-[#4a2d3d] text-white text-sm h-9"
            >
              {importingDoc ? (language === 'sv' ? 'Importerar...' : 'Importing...') : (language === 'sv' ? 'Importera dokument' : 'Import document')}
            </Button>
            
            {importingDoc && <p className="text-sm text-stone-500 mt-2">{language === 'sv' ? 'Extraherar innehåll...' : 'Extracting content...'}</p>}
          </div>
          
          <div><Label>Beskrivning (SV)</Label><RichTextEditor value={desc_sv} onChange={setDescSv} placeholder="Beskrivning på svenska..." /></div>
          <div><Label>Description (EN)</Label><RichTextEditor value={desc_en} onChange={setDescEn} placeholder="Description in English..." /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Huvudkategori' : 'Main Category'}</Label>
              <Select value={main_category_id} onValueChange={(v) => { setMainCatId(v); setSubCatId(''); }}>
                <SelectTrigger className="bg-white border-stone-300"><SelectValue placeholder={language === 'sv' ? 'Välj...' : 'Select...'} /></SelectTrigger>
                <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c.id} value={c.id}>{language === 'sv' ? c.name_sv : c.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{language === 'sv' ? 'Underkategori' : 'Subcategory'}</Label>
              <Select value={sub_category_id} onValueChange={setSubCatId} disabled={!main_category_id || subCategories.length === 0}>
                <SelectTrigger className="bg-white border-stone-300"><SelectValue placeholder={language === 'sv' ? 'Välj (valfritt)' : 'Select (optional)'} /></SelectTrigger>
                <SelectContent className="bg-white">{subCategories.map(c => <SelectItem key={c.id} value={c.id}>{language === 'sv' ? c.name_sv : c.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Removed old Image URL text input - now using image upload above */}
          <div className="flex items-center gap-2"><Switch checked={is_featured} onCheckedChange={setIsFeatured} data-testid="popular-product-switch" /><Label>{language === 'sv' ? 'Populär produkt (visas på startsidan)' : 'Popular product (shown on homepage)'}</Label></div>

          {/* Tillbehör / Cross-sell picker */}
          <div className="border border-stone-200 rounded-md p-4 bg-stone-50/50" data-testid="accessory-picker">
            <Label className="text-stone-900 font-medium">
              {language === 'sv' ? 'Tillbehör (cross-sell på produktsidan)' : 'Accessories (cross-sell on product page)'}
            </Label>
            <p className="text-xs text-stone-500 mt-1 mb-3">
              {language === 'sv'
                ? 'Välj produkter som visas under "Tillbehör & relaterade produkter" på denna produkts detaljsida.'
                : 'Pick products to show under "Accessories & related products" on this product\'s detail page.'}
            </p>

            {accessory_product_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {accessory_product_ids.map((aid) => {
                  const ap = allProducts.find(p => p.id === aid);
                  const apName = ap ? (language === 'sv' ? ap.name_sv : ap.name_en) : aid;
                  return (
                    <span key={aid} className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-stone-300 text-xs rounded-sm" data-testid={`accessory-chip-${aid}`}>
                      {apName}
                      <button
                        type="button"
                        onClick={() => setAccessoryIds(accessory_product_ids.filter(x => x !== aid))}
                        className="text-stone-400 hover:text-red-600"
                        aria-label="Remove"
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}

            <Input
              type="text"
              placeholder={language === 'sv' ? 'Sök produkt att lägga till...' : 'Search product to add...'}
              value={accessorySearch}
              onChange={(e) => setAccessorySearch(e.target.value)}
              className="bg-white border-stone-300 mb-2"
              data-testid="accessory-search-input"
            />
            {accessorySearch.trim().length >= 1 && (
              <div className="max-h-48 overflow-y-auto border border-stone-200 bg-white">
                {allProducts
                  .filter(p => {
                    if (editData && p.id === editData.id) return false;
                    if (accessory_product_ids.includes(p.id)) return false;
                    const q = accessorySearch.toLowerCase();
                    const n = (language === 'sv' ? p.name_sv : p.name_en) || '';
                    return n.toLowerCase().includes(q);
                  })
                  .slice(0, 10)
                  .map(p => {
                    const pn = language === 'sv' ? p.name_sv : p.name_en;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => { setAccessoryIds([...accessory_product_ids, p.id]); setAccessorySearch(''); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                        data-testid={`accessory-suggestion-${p.id}`}
                      >
                        {pn}
                      </button>
                    );
                  })}
                {allProducts.filter(p => {
                  if (editData && p.id === editData.id) return false;
                  if (accessory_product_ids.includes(p.id)) return false;
                  const q = accessorySearch.toLowerCase();
                  const n = (language === 'sv' ? p.name_sv : p.name_en) || '';
                  return n.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="px-3 py-2 text-xs text-stone-400">
                    {language === 'sv' ? 'Inga matchande produkter' : 'No matching products'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Certifications / Klassificeringar & märkningar picker */}
          <div className="border border-stone-200 rounded-md p-4 bg-stone-50/50" data-testid="certifications-picker">
            <Label className="text-stone-900 font-medium">
              {language === 'sv' ? 'Klassificeringar & märkningar' : 'Certifications & markings'}
            </Label>
            <p className="text-xs text-stone-500 mt-1 mb-3">
              {language === 'sv'
                ? 'Välj märkningar som visas under teknisk specifikation på produktsidan.'
                : 'Pick certifications to show under the technical specs on the product page.'}
            </p>

            {certification_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {certification_ids.map((cid) => {
                  const c = certifications.find((x) => x.id === cid);
                  return (
                    <span
                      key={cid}
                      data-testid={`cert-chip-${cid}`}
                      className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-stone-300 text-xs"
                    >
                      {c?.logo_url && (
                        <img src={c.logo_url} alt={c.name} className="w-5 h-5 object-contain" />
                      )}
                      <span>{c?.name || cid}</span>
                      <button
                        type="button"
                        onClick={() => setCertificationIds(certification_ids.filter((x) => x !== cid))}
                        className="text-stone-400 hover:text-red-600"
                        aria-label="Remove"
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}

            <Input
              type="text"
              placeholder={language === 'sv' ? 'Sök märkning (WRAS, CE, MID...)' : 'Search marking (WRAS, CE, MID...)'}
              value={certSearch}
              onChange={(e) => setCertSearch(e.target.value)}
              className="bg-white border-stone-300 mb-2"
              data-testid="cert-picker-search-input"
            />
            {certSearch.trim().length >= 1 && (
              <div className="max-h-48 overflow-y-auto border border-stone-200 bg-white">
                {certifications
                  .filter((c) => {
                    if (certification_ids.includes(c.id)) return false;
                    const q = certSearch.toLowerCase();
                    return (
                      c.name.toLowerCase().includes(q) ||
                      (c.category || '').toLowerCase().includes(q)
                    );
                  })
                  .slice(0, 10)
                  .map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => {
                        setCertificationIds([...certification_ids, c.id]);
                        setCertSearch('');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                      data-testid={`cert-suggestion-${c.id}`}
                    >
                      <img src={c.logo_url} alt={c.name} className="w-8 h-8 object-contain flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900">{c.name}</p>
                        {c.category && <p className="text-xs text-stone-500">{c.category}</p>}
                      </div>
                    </button>
                  ))}
                {certifications.filter((c) => {
                  if (certification_ids.includes(c.id)) return false;
                  const q = certSearch.toLowerCase();
                  return (
                    c.name.toLowerCase().includes(q) ||
                    (c.category || '').toLowerCase().includes(q)
                  );
                }).length === 0 && (
                  <p className="px-3 py-2 text-xs text-stone-400">
                    {language === 'sv' ? 'Inga matchningar' : 'No matches'}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter><Button type="submit" disabled={submitting} className="bg-stone-900 text-white">{submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductsTable;
export { ProductDocumentsDialog, ProductFormDialog };
